//
// # GCP Log Utilities
//
// Pure helpers for generating GCP console URLs and log messages.
//

import { Context } from "./context";

// GCP project ID for GenAlpha. Hardcoded because:
// 1. All GenAlpha deployments use the same GCP project
// 2. This is a client-safe module (CoreUtils) so we can't read server env vars
// 3. The project ID is not sensitive - it's visible in any GCP console URL
const GCP_PROJECT_ID = "generation-alpha-transistor";

// Log kind labels for GCP Cloud Logging filtering.
// Used as indexed labels (labels.logKind) for fast dashboard queries.
// Expand this as new checkpoint types are needed.
export const LogKind = {
  // Every non-ping IDE→AlphaServer request
  REQUEST: "request",
  // LLM progress updates (tool calls, thinking steps)
  PROGRESS: "progress",
  // Server-relayed observation from the IDE (sign-in attempts, runtime
  // errors, telemetry). See `AlphaServer/src/browser_events/` for the
  // ingest endpoint.
  BROWSER_EVENT: "browser-event",
} as const;

export type LogKind = (typeof LogKind)[keyof typeof LogKind];

/**
 * Build the standard fields for a checkpoint log entry.
 * Returns an object with `logKind` and `logLink` to spread into a logInfo call.
 *
 * Usage:
 *   logInfo(ctx, "Request received", { ...checkpoint(ctx, LogKind.REQUEST), api: kind });
 */
export function checkpoint(
  ctx: Context,
  kind: LogKind
): { logKind: LogKind; logLink: string } {
  return {
    logKind: kind,
    logLink: gcpLogsUrl(ctx),
  };
}

// APIs to skip logging for (noisy or uninteresting)
export const FILTERED_APIS = new Set(["get-pdk", "open", "ping"]);

/**
 * Generate a GCP Logs Explorer URL that filters by logId with a time window.
 * @param logId - The logId to filter by
 * @param timeWindowMinutes - Time window in minutes (+/- from now). Default: 10
 * @returns A URL to GCP Logs Explorer with the query pre-filled
 */
export function gcpLogsUrlForLogId(
  logId: string,
  timeWindowMinutes?: number
): string {
  const windowMinutes = timeWindowMinutes ?? 10;
  const now = new Date();
  const startTime = new Date(
    now.getTime() - windowMinutes * 60 * 1000
  ).toISOString();
  const endTime = new Date(
    now.getTime() + windowMinutes * 60 * 1000
  ).toISOString();
  const query = encodeURIComponent(
    `labels.logId="${logId}" AND timestamp>="${startTime}" AND timestamp<="${endTime}"`
  );
  return `https://console.cloud.google.com/logs/query?project=${GCP_PROJECT_ID}&query=${query}`;
}

/**
 * Generate a GCP Logs Explorer URL that filters by the context's logId with a time window.
 * @param ctx - The Context containing the logId
 * @param timeWindowMinutes - Time window in minutes (+/- from now). Default: 10
 * @returns A URL to GCP Logs Explorer with the query pre-filled
 */
export function gcpLogsUrl(ctx: Context, timeWindowMinutes?: number): string {
  const logId = ctx.toWire().logId ?? "none";
  return gcpLogsUrlForLogId(logId, timeWindowMinutes);
}

/**
 * Format a GCP log message. Returns null if no logId present.
 * @param label - The label to show in brackets, e.g. "IDE"
 * @param api - The API being called, e.g. "prompt", "open", "get-pdk"
 * @param ctx - The Context containing the logId
 * @param timeWindowMinutes - Time window in minutes (+/- from now). Default: 10
 */
export function formatGcpLogMessage(
  label: string,
  api: string,
  ctx: Context,
  timeWindowMinutes?: number
): string | null {
  const wire = ctx.toWire();
  const logId = wire.logId;
  if (!logId) {
    return null;
  }
  return `[${label}] api=${api} logId=${logId} ${gcpLogsUrlForLogId(
    logId,
    timeWindowMinutes
  )}`;
}

/**
 * Check if an API should be logged (not in filtered list).
 */
export function shouldLogApi(api: string): boolean {
  return !FILTERED_APIS.has(api);
}

/**
 * Check if an API is a heartbeat/ping that should be filtered from server logs.
 * Use this for server-side request logging where most APIs are interesting
 * but pings are noise.
 */
export function isPing(api: string): boolean {
  return api === "ping";
}
